import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';
import { environment as env} from '../environments/environment';
import { BackendAuthService } from './shared/services/backend-auth.service';
import { CognitoService, IUserInfoCognito } from './shared/services/cognito.service';
import { InactivityService } from './shared/services/inactivity.service';
import { SessionService } from './shared/services/session.service';
import { AppVersionService } from './shared/services/app-version.service';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Hub, I18n } from 'aws-amplify/utils';
import { AuthenticatorService, translations } from '@aws-amplify/ui-angular';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { NgxSpinnerService } from 'ngx-spinner';
import {MatSidenavModule} from '@angular/material/sidenav';
import { AsincronoService } from './shared/services/asincrono.service';

declare global {
  interface Window { dataLayer: any[]; }
}


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})

export class AppComponent implements OnDestroy, OnInit {

  private subscription: Subscription[] = [];
  public showContent: boolean = true;
  public ruta: string = '';

  events: string[] = [];
  opened: boolean = true;

  /*private publicRoutes = [
    'mfa-tutorial'
  ];
  public isPrivateRoute = true;*/

  private pollingServiciosSub?: Subscription;

  constructor(
    private inactivityService: InactivityService,
    private backendAuthService: BackendAuthService,
    private _cognitoService: CognitoService,
    private router: Router,
    private _sessionService: SessionService,
    private activedRoute: ActivatedRoute,
    public dialog: MatDialog,
    private _versionService: AppVersionService,
    public authenticator: AuthenticatorService,
    private spinner: NgxSpinnerService,
    private asincronoService: AsincronoService,
    ) {

    if(translations['es']) {
      translations['es']['Enter your username'] = 'Ingrese su nombre de usuario';
      translations['es']['Enter your Username'] = 'Ingrese su Nombre de Usuario';
    }
    I18n.putVocabularies(translations);
    I18n.setLanguage('es')
    this.listenToAuthEvents();
    this.setStore();
    this.userActive();

    this.subscription.push(this.router.events
    .pipe(filter(event => event instanceof NavigationEnd),
      map(() => this.activedRoute.snapshot),
      map(route => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      })
    )
    .subscribe((route: ActivatedRouteSnapshot) => {
      this.ruta = route.data.breadcrumb;
      const spanExtra: any = document.getElementById('span-menutoggle');
      if (spanExtra)
        spanExtra.textContent = '';

      //const currentPath = route.routeConfig?.path || '';
      //this.isPrivateRoute = !this.publicRoutes.includes(currentPath);
    }));

    this.verifyToken();
    this.addUserIdDataLayer();
  }

  listenToAuthEvents() {
    Hub.listen('auth', async ({ payload } : any) => {
      switch (payload.event) {
        case 'signedIn':
          this.showContent = false;
          this.setStore();
          this.userActive();
          this.manageAppVersionStep1();
          window.location.reload();
          break;
        
        case 'tokenRefresh':
          //Ponemos el nuevo token en el local storage para que lo consulte verifyToken
          const { accessToken, idToken } = (await fetchAuthSession()).tokens ?? {};
          if(idToken) {
            this._sessionService.setCurrentIdToken(idToken.toString());
          }
          break;
        
        case 'signedOut':
          this.stopWatching();
          this.asincronoService.stopPollingEstadoServicios();

          if (this.pollingServiciosSub) {
            this.pollingServiciosSub.unsubscribe();
          }
      }
    })
  }

  ngOnInit(): void {
    if(localStorage.getItem('shouldLogout')) {
      localStorage.removeItem('shouldLogout');
      this._cognitoService.logout();
    }

    this.checkInitialAuthStatus();

    if(this.authenticator.authStatus === 'authenticated' && !this._sessionService.getOrganization()) {
      this.router.navigate(['/organizacion']);
    }
  }

  async checkInitialAuthStatus() {
    try {
      await getCurrentUser();

      this.initPollingEstadoServicios();
    } catch (error) {
      console.debug('Usuario no autenticado o sesión inactiva al iniciar app:', error);
    }
  }

  async setStore() {
    //Ponemos el nuevo token en el local storage para que lo consulte verifyToken
    const { accessToken, idToken } = (await fetchAuthSession()).tokens ?? {};
    if(idToken) {
      this._sessionService.setCurrentIdToken(idToken.toString());
    }
    const idTokenPayload = idToken?.payload || {};
    const selectedOrganization = this._sessionService.getOrganization();

    localStorage.setItem('userInfo', JSON.stringify(<IUserInfoCognito>{
      organizations: idTokenPayload["cognito:groups"],
      username: idTokenPayload["email"],
      email: idTokenPayload["email"],
      userSub: idTokenPayload["sub"],
      given_name: idTokenPayload["given_name"],
      name: idTokenPayload["name"],
      phoneNumber: idTokenPayload["phone_number"]
    }));
    window.dispatchEvent(new Event('storage'));

  }

  /**
   * @description
   * Funcion que inicia o reinicia el observador de inactividad cada vez que el usuario mueve el
   * mouse o da click en cualquier parte de la pagina
   */
  async userActive(): Promise<void> {
    const { accessToken, idToken } = (await fetchAuthSession()).tokens ?? {};
    if (!this.inactivityService.modalIsOpen() && idToken) { //chequeo que el usuario este logeado y no est el modal abierto para empezar a contar
      this.inactivityService.initInactive();
    }
  }

  /**
   * @description
   * Verifica si el token es ha expirado cada N tiempo segun @param env.tokenObs, si lo esta cierra sesion
   */
  verifyToken(): void {
    const timer = setInterval(async () => {
      if (await this._cognitoService.tokenValidator()) { //Si devuelve true, se hace el logout
        clearInterval(timer);
        this.inactivityService.onStopWatching();
        this._cognitoService.logout();
      }
    }, env.tokenObs);
  }

  ngOnDestroy() {
    for (const item of this.subscription) {
      item.unsubscribe();
    }

    this.asincronoService.stopPollingEstadoServicios();
    if (this.pollingServiciosSub) {
      this.pollingServiciosSub.unsubscribe();
    }
  }

  changeNameUrl(url: string) {
    switch (url) {
      case 'buscarR':
        return 'buscar';
      case 'listar':
        return 'administración de usuarios';
      case 'usuarios':
        return 'configuraciones';
      default:
        return url? url.replace(/-/g, ' '): '';
    }
  }

  addUserIdDataLayer(): void {
    const dataLayer = window.dataLayer;
    const username = this._sessionService.getUserInfo()?.username
    if(dataLayer && username){
      dataLayer.push({
        'user_id': username
      });
    }
  }

  manageAppVersionStep1() {
    // 2. Chequear en la local storage si la variable version esta vacia o no
    let appVersion: string | null = this._sessionService.getAppVersion();
    // 3. esta vacia? entonces la voy a buscar al endpoint y la actualizamos en el local
    if(!appVersion || appVersion == "") {
      this._versionService.getLastDeployedVersion().subscribe((deployVersion: any) => {
        if(deployVersion.version) {
          this._sessionService.setAppVersion(deployVersion.version);
          this.manageAppVersionStep2();
        }
      }, error => {
        console.error("Error al obtener la version", error?.message || '');
      })
    } else {
      // 4. no esta vacia? Fin
      this.manageAppVersionStep2();
    }
  }

  manageAppVersionStep2() {

    interval(60 * 4 * 1000) // cada 4 min
    .pipe(
        mergeMap(() => this._versionService.getLastDeployedVersion())
    )
    .subscribe((deployVersion: any) => {
      let localAppVersion: string | null = this._sessionService.getAppVersion();
      if(!localAppVersion) {
          this.openLogoutDialog();
      }
      if(deployVersion.version && deployVersion.version !== localAppVersion) {
        this._sessionService.setAppVersion(deployVersion.version);
        this.openLogoutDialog();
      }
    })
  }

  openLogoutDialog() {
    // Espero 2 min
    // Esto es para que no haga un logout justo en el mismo instante que se esta terminando de deployar y se cuelgue
    setTimeout(() => {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Nueva versión',
          message: `Hay una nueva version del sitio, por favor cierre sesión y vuelva a ingresar`,
          textBoton: ['Ok'],
          textTimer: 0,
          hideCancelmButtom: true
        }
      });

      dialogRef.afterClosed().subscribe(() => {
        sessionStorage.setItem('shouldRefresh', 'true')
        this.reload();
      });
    }, 120 * 1000)
  }

  reload() {
    sessionStorage.setItem('shouldLogout', 'true')
    location.reload();
  }

  stopWatching() {
    this.inactivityService.onStopWatching();
  }

  initPollingEstadoServicios() {
    this.pollingServiciosSub = this.asincronoService.iniciarPollingEstadoServicios().subscribe();
  }
}

